class DcProductGallery extends HTMLElement {
  connectedCallback() {
    this.previousButton = this.querySelector('[data-dc-product-gallery-prev]');
    this.nextButton = this.querySelector('[data-dc-product-gallery-next]');
    this.thumbnailList = this.querySelector('[data-dc-product-gallery-thumbnails]');

    if (!this.previousButton || !this.nextButton || !this.thumbnailList) {
      return;
    }

    this.previousButton.addEventListener('click', () => this.navigate(-1));
    this.nextButton.addEventListener('click', () => this.navigate(1));
    this.thumbnailList.addEventListener('click', () => requestAnimationFrame(() => this.update()));

    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this.thumbnailList, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get visibleThumbnails() {
    return Array.from(this.thumbnailList.querySelectorAll('.product-gallery__thumbnail:not(.is-filtered)'));
  }

  navigate(direction) {
    const thumbnails = this.visibleThumbnails;
    const selectedIndex = thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-nav-selected'));
    const targetIndex = Math.min(Math.max(selectedIndex + direction, 0), thumbnails.length - 1);
    const target = thumbnails[targetIndex];

    if (!target || targetIndex === selectedIndex) {
      return;
    }

    target.click();
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center'
    });

    requestAnimationFrame(() => this.update());
  }

  update() {
    const thumbnails = this.visibleThumbnails;
    const selectedIndex = thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-nav-selected'));
    const hasSelection = selectedIndex >= 0;

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-current', index === selectedIndex ? 'true' : 'false');
    });

    this.previousButton.disabled = !hasSelection || selectedIndex === 0;
    this.nextButton.disabled = !hasSelection || selectedIndex === thumbnails.length - 1;
  }
}

class DcProductVariantSelector extends HTMLElement {
  static openStates = new Map();

  connectedCallback() {
    this.disclosures = Array.from(this.querySelectorAll('[data-dc-variant-disclosure]'));
    this.stateKey = this.dataset.stateKey;
    this.singleOpen = this.dataset.singleOpen === 'true';
    this.handleToggle = this.onToggle.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleChange = this.onChange.bind(this);

    if (!Shopify.designMode && this.stateKey && DcProductVariantSelector.openStates.has(this.stateKey)) {
      const openPositions = DcProductVariantSelector.openStates.get(this.stateKey);
      this.restoringState = true;

      this.disclosures.forEach((disclosure) => {
        disclosure.open = openPositions.has(disclosure.dataset.optionPosition);
      });

      requestAnimationFrame(() => {
        this.restoringState = false;
        this.syncExpandedState();
      });
    }

    this.disclosures.forEach((disclosure) => disclosure.addEventListener('toggle', this.handleToggle));
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('change', this.handleChange);
    this.syncExpandedState();
  }

  disconnectedCallback() {
    this.disclosures?.forEach((disclosure) => disclosure.removeEventListener('toggle', this.handleToggle));
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('change', this.handleChange);
  }

  onChange(event) {
    const input = event.target.closest('input[data-option-position]:checked');

    if (!input || !this.contains(input)) {
      return;
    }

    const option = input.closest('.dc-product-main__option');
    const selectedChoice = input.nextElementSibling;
    const currentValue = option?.querySelector('.dc-product-main__option-current');
    const selectedLabel = selectedChoice?.getAttribute('title') || input.value;

    if (currentValue) {
      currentValue.textContent = selectedLabel;
    }

    const swatchPreview = option?.querySelector('[data-dc-selected-swatch-preview]');

    if (swatchPreview && selectedChoice?.classList.contains('color-swatch__item')) {
      swatchPreview.style.cssText = selectedChoice.style.cssText;
      swatchPreview.classList.toggle(
        'is-unavailable',
        selectedChoice.closest('.color-swatch')?.classList.contains('color-swatch--disabled') ?? false
      );
    }

    const imagePreview = option?.querySelector('.dc-product-main__option-preview-item img');
    const selectedImage = selectedChoice?.querySelector('.dc-product-main__variant-image img');

    if (imagePreview && selectedImage) {
      ['src', 'srcset', 'sizes', 'width', 'height'].forEach((attribute) => {
        if (selectedImage.hasAttribute(attribute)) {
          imagePreview.setAttribute(attribute, selectedImage.getAttribute(attribute));
        } else {
          imagePreview.removeAttribute(attribute);
        }
      });
    }
  }

  onKeydown(event) {
    if (event.key !== 'Enter') {
      return;
    }

    const summary = event.target.closest('[data-dc-variant-disclosure] > summary');

    if (!summary || !this.contains(summary)) {
      return;
    }

    event.preventDefault();
    summary.click();
  }

  onToggle(event) {
    if (this.restoringState) {
      return;
    }

    const disclosure = event.currentTarget;

    if (disclosure.open && this.singleOpen) {
      this.disclosures.forEach((otherDisclosure) => {
        if (otherDisclosure !== disclosure) {
          otherDisclosure.open = false;
        }
      });
    }

    this.syncExpandedState();
    this.rememberOpenState();
  }

  syncExpandedState() {
    this.disclosures.forEach((disclosure) => {
      const summary = disclosure.querySelector(':scope > summary');
      summary?.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
    });
  }

  rememberOpenState() {
    if (Shopify.designMode || !this.stateKey) {
      return;
    }

    const openPositions = new Set(
      this.disclosures
        .filter((disclosure) => disclosure.open)
        .map((disclosure) => disclosure.dataset.optionPosition)
    );

    DcProductVariantSelector.openStates.set(this.stateKey, openPositions);
  }
}

class DcProductPaymentOptions extends HTMLElement {
  static activeInstance = null;

  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-dc-payment-trigger]'));
    this.layers = Array.from(this.querySelectorAll('[data-dc-payment-layer]'));

    if (this.triggers.length === 0 || this.layers.length === 0) {
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(trigger), { signal });
    });

    this.layers.forEach((layer) => {
      layer.querySelectorAll('[data-dc-payment-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), { signal });
      });

      layer.addEventListener('click', (event) => {
        if (event.target.matches('[data-dc-payment-overlay]')) {
          this.close();
        }
      }, { signal });

      document.body.appendChild(layer);
    });

    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();

    if (DcProductPaymentOptions.activeInstance === this) {
      this.close({ restoreFocus: false });
    }

    this.layers?.forEach((layer) => layer.remove());
  }

  open(trigger) {
    const targetId = trigger.getAttribute('aria-controls');
    const layer = this.layers.find((candidate) => candidate.id === targetId);

    if (!layer) {
      return;
    }

    if (DcProductPaymentOptions.activeInstance && DcProductPaymentOptions.activeInstance !== this) {
      DcProductPaymentOptions.activeInstance.close({ restoreFocus: false });
    } else if (this.activeLayer) {
      this.close({ restoreFocus: false });
    }

    this.activeTrigger = trigger;
    this.activeLayer = layer;
    this.activeModal = layer.querySelector('[data-dc-payment-modal]');
    DcProductPaymentOptions.activeInstance = this;
    layer.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('dc-product-payment-open');

    requestAnimationFrame(() => {
      const initialFocus = layer.querySelector('[data-dc-payment-close]') || this.activeModal;
      initialFocus?.focus({ preventScroll: true });
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.activeLayer || this.activeLayer.hidden) {
      return;
    }

    this.activeLayer.hidden = true;
    this.activeTrigger?.setAttribute('aria-expanded', 'false');

    if (DcProductPaymentOptions.activeInstance === this) {
      DcProductPaymentOptions.activeInstance = null;
      document.documentElement.classList.remove('dc-product-payment-open');
    }

    if (restoreFocus && this.activeTrigger?.isConnected) {
      this.activeTrigger.focus({ preventScroll: true });
    }

    this.activeLayer = null;
    this.activeModal = null;
    this.activeTrigger = null;
  }

  onKeydown(event) {
    if (DcProductPaymentOptions.activeInstance !== this || !this.activeModal) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = Array.from(this.activeModal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.getClientRects().length > 0);

    if (focusable.length === 0) {
      event.preventDefault();
      this.activeModal.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
class DcProductAccessories extends HTMLElement {
  static activeInstance = null;

  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.triggers = Array.from(this.querySelectorAll('[data-dc-accessory-trigger]'));
    this.layers = Array.from(this.querySelectorAll('[data-dc-accessory-layer]'));
    this.viewport = this.querySelector('[data-dc-accessories-viewport]');
    this.previousButton = this.querySelector('[data-dc-accessories-prev]');
    this.nextButton = this.querySelector('[data-dc-accessories-next]');

    this.triggers.forEach((trigger) => {
      trigger.dataset.defaultLabel = trigger.getAttribute('aria-label') || '';
      trigger.addEventListener('click', () => this.open(trigger), { signal });
    });

    this.layers.forEach((layer) => {
      try {
        layer.dcVariants = JSON.parse(layer.querySelector('[data-dc-accessory-variant-data]')?.textContent || '[]');
      } catch (error) {
        layer.dcVariants = [];
      }

      layer.querySelectorAll('[data-dc-accessory-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), { signal });
      });
      layer.addEventListener('click', (event) => {
        if (event.target.matches('[data-dc-accessory-overlay]')) {
          this.close();
        }
      }, { signal });
      layer.querySelectorAll('[data-dc-accessory-option]').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant(layer), { signal });
      });
      layer.querySelector('[data-dc-accessory-add]')?.addEventListener('click', (event) => {
        this.addAccessory(event.currentTarget, layer);
      }, { signal });
      this.initializeGallery(layer.querySelector('[data-dc-accessory-gallery]'), signal);
      document.body.appendChild(layer);
    });

    this.initializeCarousel(signal);
    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    if (DcProductAccessories.activeInstance === this) {
      this.close({ restoreFocus: false });
    }
    this.layers?.forEach((layer) => layer.remove());
  }

  initializeCarousel(signal) {
    if (!this.viewport || !this.previousButton || !this.nextButton) {
      return;
    }

    this.previousButton.addEventListener('click', () => this.navigateCarousel(-1), { signal });
    this.nextButton.addEventListener('click', () => this.navigateCarousel(1), { signal });
    this.viewport.addEventListener('scroll', () => {
      if (this.carouselFrame) return;
      this.carouselFrame = requestAnimationFrame(() => {
        this.carouselFrame = null;
        this.updateCarouselControls();
      });
    }, { signal, passive: true });
    this.viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigateCarousel(event.key === 'ArrowRight' ? 1 : -1);
      }
    }, { signal });

    this.resizeObserver = new ResizeObserver(() => this.updateCarouselControls());
    this.resizeObserver.observe(this.viewport);
    this.updateCarouselControls();
  }

  navigateCarousel(direction) {
    const firstCard = this.viewport.querySelector('[data-dc-accessory-card]');
    if (!firstCard) return;
    const list = this.viewport.querySelector('.dc-product-accessories__list');
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const step = firstCard.getBoundingClientRect().width + gap;
    this.viewport.scrollBy({
      left: direction * step,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  updateCarouselControls() {
    if (!this.viewport) return;
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    this.previousButton.disabled = this.viewport.scrollLeft <= 1;
    this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 1;
  }

  initializeGallery(gallery, signal) {
    if (!gallery) return;
    const track = gallery.querySelector('[data-dc-accessory-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('.dc-product-accessories__gallery-slide'));
    const dots = Array.from(gallery.querySelectorAll('[data-dc-accessory-gallery-dot]'));
    if (!track || slides.length === 0) return;

    gallery.dcGallery = { track, slides, dots, index: 0, dragging: false };
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToGallerySlide(gallery, index), { signal });
    });
    track.addEventListener('scroll', () => {
      if (gallery.dcGallery.frame) return;
      gallery.dcGallery.frame = requestAnimationFrame(() => {
        gallery.dcGallery.frame = null;
        const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        this.updateGalleryDots(gallery, index);
      });
    }, { signal, passive: true });
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      gallery.dcGallery.dragging = true;
      gallery.dcGallery.startX = event.clientX;
      gallery.dcGallery.startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    }, { signal });
    track.addEventListener('pointermove', (event) => {
      if (!gallery.dcGallery.dragging) return;
      event.preventDefault();
      track.scrollLeft = gallery.dcGallery.startScroll - (event.clientX - gallery.dcGallery.startX);
    }, { signal });
    const finishDrag = (event) => {
      if (!gallery.dcGallery.dragging) return;
      gallery.dcGallery.dragging = false;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      this.goToGallerySlide(gallery, index);
    };
    track.addEventListener('pointerup', finishDrag, { signal });
    track.addEventListener('pointercancel', finishDrag, { signal });
  }

  goToGallerySlide(gallery, index, behavior = null) {
    const state = gallery?.dcGallery;
    if (!state) return;
    const safeIndex = Math.min(Math.max(index, 0), state.slides.length - 1);
    state.track.scrollTo({
      left: safeIndex * state.track.clientWidth,
      behavior: behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth')
    });
    this.updateGalleryDots(gallery, safeIndex);
  }

  updateGalleryDots(gallery, index) {
    const state = gallery?.dcGallery;
    if (!state) return;
    state.index = Math.min(Math.max(index, 0), state.slides.length - 1);
    state.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === state.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  open(trigger) {
    const layer = this.layers.find((candidate) => candidate.id === trigger.getAttribute('aria-controls'));
    if (!layer) return;

    if (DcProductAccessories.activeInstance && DcProductAccessories.activeInstance !== this) {
      DcProductAccessories.activeInstance.close({ restoreFocus: false });
    } else if (this.activeLayer) {
      this.close({ restoreFocus: false });
    }

    this.activeTrigger = trigger;
    this.activeLayer = layer;
    this.activeModal = layer.querySelector('[data-dc-accessory-modal]');
    DcProductAccessories.activeInstance = this;
    layer.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('dc-product-accessories-open');

    requestAnimationFrame(() => {
      layer.querySelector('[data-dc-accessory-close]')?.focus({ preventScroll: true });
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.activeLayer || this.activeLayer.hidden) return;
    this.activeLayer.hidden = true;
    this.activeTrigger?.setAttribute('aria-expanded', 'false');
    if (DcProductAccessories.activeInstance === this) {
      DcProductAccessories.activeInstance = null;
      document.documentElement.classList.remove('dc-product-accessories-open');
    }
    if (restoreFocus && this.activeTrigger?.isConnected) {
      this.activeTrigger.focus({ preventScroll: true });
    }
    this.activeLayer = null;
    this.activeModal = null;
    this.activeTrigger = null;
  }

  updateVariant(layer) {
    const selectedOptions = Array.from(layer.querySelectorAll('[data-dc-accessory-option]')).map((select) => select.value);
    const variant = layer.dcVariants.find((candidate) => candidate.options.every((value, index) => value === selectedOptions[index]));
    const addButton = layer.querySelector('[data-dc-accessory-add]');
    const label = addButton?.querySelector('[data-dc-accessory-add-label]');
    const price = layer.querySelector('[data-dc-accessory-price]');
    const availability = layer.querySelector('[data-dc-accessory-availability]');
    const error = layer.querySelector('[data-dc-accessory-error]');

    if (error) error.hidden = true;
    if (!variant) {
      if (addButton) addButton.disabled = true;
      if (label) label.textContent = addButton?.dataset.unavailableText || 'Agotado';
      if (availability) {
        availability.textContent = 'Combinación no disponible';
        availability.classList.add('is-unavailable');
      }
      return;
    }

    if (addButton) {
      addButton.dataset.variantId = variant.id;
      addButton.disabled = !variant.available;
    }
    if (label) label.textContent = variant.available ? addButton.dataset.availableText : addButton.dataset.unavailableText;
    if (price) price.textContent = variant.priceFormatted;
    if (availability) {
      availability.textContent = variant.available ? 'Disponible' : 'Agotado';
      availability.classList.toggle('is-unavailable', !variant.available);
    }

    if (variant.featuredMediaId) {
      const gallery = layer.querySelector('[data-dc-accessory-gallery]');
      const index = gallery?.dcGallery?.slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(variant.featuredMediaId));
      if (index >= 0) this.goToGallerySlide(gallery, index);
    }
  }

  async addAccessory(button, layer) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;
    const errorElement = layer.querySelector('[data-dc-accessory-error]');
    const variant = layer.dcVariants.find((candidate) => Number(candidate.id) === variantId) || null;
    button.disabled = true;
    if (errorElement) errorElement.hidden = true;
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    try {
      const response = await fetch(`${window.routes.cartAddUrl}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'No se pudo agregar el accesorio.');

      this.markAdded(layer.dataset.productId);
      this.close({ restoreFocus: false });
      this.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { button, variant, quantity: 1, productAdded: true }
      }));
    } catch (error) {
      button.disabled = !variant?.available;
      if (errorElement) {
        errorElement.textContent = error.message;
        errorElement.hidden = false;
      }
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    }
  }

  markAdded(productId) {
    const card = this.querySelector(`[data-dc-accessory-card][data-product-id="${CSS.escape(String(productId))}"]`);
    const trigger = card?.querySelector('[data-dc-accessory-trigger]');
    card?.classList.add('is-added');
    if (trigger) {
      trigger.setAttribute('aria-label', `Agregado. ${trigger.dataset.defaultLabel}`);
      trigger.setAttribute('aria-pressed', 'true');
    }
  }

  onKeydown(event) {
    if (DcProductAccessories.activeInstance !== this || !this.activeModal) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(this.activeModal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (focusable.length === 0) {
      event.preventDefault();
      this.activeModal.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
class DcProductBundle extends HTMLElement {
  static activeInstance = null;

  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.initializeBulk(signal);

    this.openButton = this.querySelector('[data-dc-bundle-open]');
    this.layer = this.querySelector('[data-dc-bundle-layer]');
    if (!this.openButton || !this.layer) return;
    this.modal = this.layer.querySelector('[data-dc-bundle-modal]');
    this.steps = Array.from(this.layer.querySelectorAll('[data-dc-bundle-step]'));
    this.progressButtons = Array.from(this.layer.querySelectorAll('[data-dc-bundle-progress]'));
    this.backButton = this.layer.querySelector('[data-dc-bundle-back]');
    this.nextButton = this.layer.querySelector('[data-dc-bundle-next]');
    this.addAllButton = this.layer.querySelector('[data-dc-bundle-add-all]');
    this.errorElement = this.layer.querySelector('[data-dc-bundle-error]');
    this.summaryElement = this.layer.querySelector('[data-dc-bundle-summary]');
    this.bundleVariants = this.parseJson('[data-dc-bundle-variants]', []);
    this.bundleMeta = this.parseJson('[data-dc-bundle-meta]', {});
    this.visualVariants = {
      1: this.parseJson('[data-dc-bundle-visual-data="1"]', []),
      2: this.parseJson('[data-dc-bundle-visual-data="2"]', [])
    };
    this.selectedExtras = { addons: new Map(), accessories: new Map() };
    this.visualSelections = { 1: {}, 2: {} };
    this.layer.querySelectorAll('[data-dc-bundle-visual-option]').forEach((fieldset) => {
      const component = Number(fieldset.dataset.component);
      const position = Number(fieldset.dataset.optionPosition);
      this.visualSelections[component][position] = fieldset.querySelector('input:checked')?.value || '';
    });
    this.currentStep = 0;

    this.openButton.addEventListener('click', () => this.open(), { signal });
    this.layer.querySelectorAll('[data-dc-bundle-close]').forEach((button) => button.addEventListener('click', () => this.close(), { signal }));
    this.layer.addEventListener('click', (event) => {
      if (event.target.matches('[data-dc-bundle-overlay]')) this.close();
    }, { signal });
    this.layer.querySelectorAll('[data-dc-bundle-option] input').forEach((input) => input.addEventListener('change', (event) => this.updateBundleVariant(event), { signal }));
    this.layer.querySelectorAll('[data-dc-bundle-visual-option] input').forEach((input) => input.addEventListener('change', (event) => this.updateVisualSelection(event), { signal }));
    this.progressButtons.forEach((button) => button.addEventListener('click', () => this.goToStep(Number(button.dataset.stepIndex)), { signal }));
    this.backButton.addEventListener('click', () => this.goToStep(this.currentStep - 1), { signal });
    this.nextButton.addEventListener('click', () => this.goToStep(this.currentStep + 1), { signal });
    this.addAllButton.addEventListener('click', () => this.addAllToCart(), { signal });
    this.layer.querySelectorAll('[data-dc-bundle-extra-action]').forEach((button) => button.addEventListener('click', () => this.handleExtraAction(button), { signal }));
    this.layer.querySelectorAll('[data-dc-bundle-quick-close]').forEach((button) => button.addEventListener('click', () => this.closeQuick(), { signal }));
    this.layer.querySelectorAll('[data-dc-bundle-quick-option]').forEach((select) => select.addEventListener('change', () => this.updateQuickVariant(select.closest('[data-dc-bundle-quick]')), { signal }));
    this.layer.querySelectorAll('[data-dc-bundle-quick-select]').forEach((button) => button.addEventListener('click', () => this.selectQuickVariant(button.closest('[data-dc-bundle-quick]')), { signal }));
    this.initializeExtraCarousels(signal);
    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

    this.selectedVariant = this.resolveBundleVariant();
    this.updateBundlePresentation();
    this.updateStepState();
    document.body.appendChild(this.layer);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObservers?.forEach((observer) => observer.disconnect());
    if (DcProductBundle.activeInstance === this) this.close({ restoreFocus: false });
    this.layer?.remove();
  }

  parseJson(selector, fallback) {
    try { return JSON.parse(this.layer.querySelector(selector)?.textContent || JSON.stringify(fallback)); }
    catch (error) { return fallback; }
  }

  initializeBulk(signal) {
    this.bulkDetails = this.querySelector('[data-dc-bulk-details]');
    if (!this.bulkDetails) return;

    try {
      this.bulkConfig = JSON.parse(this.bulkDetails.querySelector('[data-dc-bulk-config]')?.textContent || '{}');
    } catch (error) {
      this.bulkConfig = {};
    }

    this.bulkInput = this.bulkDetails.querySelector('[data-dc-bulk-quantity]');
    this.bulkTiers = (this.bulkConfig.tiers || []).map((tier, index) => {
      const start = Math.max(1, Math.floor(Number(tier.start) || 1));
      const end = tier.end == null ? null : Math.max(start, Math.floor(Number(tier.end) || start));
      return { index, start, end, discount: Math.min(100, Math.max(0, Number(tier.discount) || 0)) };
    });
    this.bulkStateKey = `${this.dataset.productFormId || 'product'}:${this.bulkConfig.productUrl || location.pathname}`;
    window.dcBulkQuoteState = window.dcBulkQuoteState || new Map();
    const cachedState = window.dcBulkQuoteState.get(this.bulkStateKey) || {};
    this.bulkQuantity = this.normalizeBulkQuantity(cachedState.quantity || 1);
    this.bulkDetails.open = Boolean(cachedState.open);

    this.bulkDetails.addEventListener('toggle', () => this.storeBulkState(), { signal });
    this.bulkDetails.querySelector('[data-dc-bulk-minus]')?.addEventListener('click', () => this.setBulkQuantity(this.bulkQuantity - 1), { signal });
    this.bulkDetails.querySelector('[data-dc-bulk-plus]')?.addEventListener('click', () => this.setBulkQuantity(this.bulkQuantity + 1), { signal });
    this.bulkInput?.addEventListener('input', () => {
      if (this.bulkInput.value === '') return;
      this.setBulkQuantity(this.bulkInput.value);
    }, { signal });
    this.bulkInput?.addEventListener('change', () => this.setBulkQuantity(this.bulkInput.value), { signal });
    this.bulkDetails.querySelector('[data-dc-bulk-quote]')?.addEventListener('click', (event) => {
      if (event.currentTarget.getAttribute('aria-disabled') === 'true') event.preventDefault();
    }, { signal });
    this.bulkDetails.querySelector('[data-dc-bulk-help-link]')?.addEventListener('click', (event) => {
      if (event.currentTarget.getAttribute('aria-disabled') === 'true') event.preventDefault();
    }, { signal });
    document.addEventListener('variant:changed', (event) => this.updateBulkVariant(event), { signal });
    this.updateBulkPresentation();
  }

  normalizeBulkQuantity(value) {
    const quantity = Math.floor(Number(value));
    return Number.isFinite(quantity) ? Math.max(1, quantity) : 1;
  }

  setBulkQuantity(value) {
    this.bulkQuantity = this.normalizeBulkQuantity(value);
    this.storeBulkState();
    this.updateBulkPresentation();
  }

  storeBulkState() {
    if (!this.bulkStateKey) return;
    window.dcBulkQuoteState?.set(this.bulkStateKey, { quantity: this.bulkQuantity, open: Boolean(this.bulkDetails?.open) });
  }

  updateBulkVariant(event) {
    if (!this.bulkConfig || event.detail?.formId !== this.dataset.productFormId || !event.detail.variant) return;
    const variant = event.detail.variant;
    this.bulkConfig.variant = { id: variant.id, title: variant.title, sku: variant.sku, price: Number(variant.price) || 0 };
    this.updateBulkPresentation();
  }

  getActiveBulkTier(quantity) {
    return [...this.bulkTiers].reverse().find((tier) => quantity >= tier.start && (tier.end == null || quantity <= tier.end)) || null;
  }

  getBulkCalculation() {
    const quantity = this.bulkQuantity;
    const regularPrice = Number(this.bulkConfig?.variant?.price) || 0;
    const activeTier = this.getActiveBulkTier(quantity);
    const discount = activeTier?.discount || 0;
    const unitPrice = Math.round(regularPrice * (100 - discount) / 100);
    const regularTotal = regularPrice * quantity;
    const total = Math.round(regularTotal * (100 - discount) / 100);
    return { quantity, regularPrice, activeTier, discount, unitPrice, total, savings: Math.max(0, regularTotal - total) };
  }

  updateBulkPresentation() {
    if (!this.bulkDetails || !this.bulkConfig) return;
    const calculation = this.getBulkCalculation();
    if (this.bulkInput && this.bulkInput.value !== String(calculation.quantity)) this.bulkInput.value = calculation.quantity;
    const minus = this.bulkDetails.querySelector('[data-dc-bulk-minus]');
    if (minus) minus.disabled = calculation.quantity <= 1;

    this.bulkTiers.forEach((tier) => {
      const unitPrice = Math.round(calculation.regularPrice * (100 - tier.discount) / 100);
      const cell = this.bulkDetails.querySelector(`[data-dc-bulk-tier="${tier.index}"]`);
      const label = this.bulkDetails.querySelector(`[data-dc-bulk-tier-label="${tier.index}"]`);
      if (cell) cell.textContent = this.formatBulkMoney(unitPrice);
      cell?.classList.toggle('is-active', calculation.activeTier?.index === tier.index);
      label?.classList.toggle('is-active', calculation.activeTier?.index === tier.index);
    });

    const bestTier = this.bulkTiers.reduce((best, tier) => !best || tier.discount > best.discount ? tier : best, null);
    const bestUnitPrice = Math.round(calculation.regularPrice * (100 - (bestTier?.discount || 0)) / 100);
    const closedPrice = this.bulkDetails.querySelector('[data-dc-bulk-closed-price]');
    if (closedPrice) closedPrice.textContent = `${this.formatBulkMoney(bestUnitPrice)}/unidad`;
    const total = this.bulkDetails.querySelector('[data-dc-bulk-total]');
    if (total) total.textContent = this.formatBulkMoney(calculation.total);
    const save = this.bulkDetails.querySelector('[data-dc-bulk-save]');
    const saveValue = this.bulkDetails.querySelector('[data-dc-bulk-save-value]');
    if (saveValue) saveValue.textContent = this.formatBulkMoney(calculation.savings);
    if (save) save.hidden = calculation.savings <= 0;

    const quoteLink = this.bulkDetails.querySelector('[data-dc-bulk-quote]');
    this.updateBulkLink(quoteLink, this.buildBulkMessage(calculation));
    const helpLink = this.bulkDetails.querySelector('[data-dc-bulk-help-link]');
    this.updateBulkLink(helpLink, String(this.bulkConfig.helpMessage || '').trim());
    const status = this.bulkDetails.querySelector('[data-dc-bulk-status]');
    if (status) status.textContent = `${calculation.quantity} unidades. ${calculation.discount}% de descuento. Total estimado ${this.formatBulkMoney(calculation.total)}.`;
  }

  updateBulkLink(link, message) {
    if (!link) return;
    const whatsappUrl = this.buildWhatsAppUrl(message);
    if (whatsappUrl) {
      link.href = whatsappUrl;
      link.removeAttribute('aria-disabled');
    } else {
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
    }
  }

  buildWhatsAppUrl(message) {
    const number = String(this.bulkConfig?.whatsappNumber || '').replace(/\D/g, '');
    if (!number || !message) return null;
    return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
  }

  buildBulkMessage(calculation) {
    const variant = this.bulkConfig.variant || {};
    const lines = [];
    const baseMessage = String(this.bulkConfig.messageBase || '').trim();
    if (baseMessage) lines.push(baseMessage, '');
    lines.push(`Producto: ${this.bulkConfig.productTitle || document.title}`);
    if (variant.title && variant.title !== 'Default Title') lines.push(`Variante: ${variant.title}`);
    if (variant.sku) lines.push(`SKU: ${variant.sku}`);
    lines.push(
      `Cantidad: ${calculation.quantity}`,
      `Precio regular: ${this.formatBulkMoney(calculation.regularPrice)}/u`,
      `Descuento por volumen: ${calculation.discount}%`,
      `Precio estimado: ${this.formatBulkMoney(calculation.unitPrice)}/u`,
      `Ahorro estimado: ${this.formatBulkMoney(calculation.savings)}`,
      `Total estimado: ${this.formatBulkMoney(calculation.total)}`,
      '',
      `Producto: ${this.getBulkProductUrl(variant.id)}`
    );
    return lines.join('\n');
  }

  getBulkProductUrl(variantId) {
    const url = new URL(this.bulkConfig.productUrl || location.pathname, location.origin);
    if (variantId) url.searchParams.set('variant', variantId);
    return url.toString();
  }

  formatBulkMoney(cents) {
    const format = window.theme?.moneyFormat || '{{amount}}';
    const value = Number(cents) || 0;
    const formatNumber = (decimals, thousands, decimal) => {
      const fixed = (value / 100).toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    return format.replace(/\{\{\s*(amount_no_decimals_with_comma_separator|amount_with_comma_separator|amount_no_decimals|amount)\s*\}\}/, (match, token) => {
      if (token === 'amount_no_decimals') return formatNumber(0, ',', '.');
      if (token === 'amount_no_decimals_with_comma_separator') return formatNumber(0, '.', ',');
      if (token === 'amount_with_comma_separator') return formatNumber(2, '.', ',');
      return formatNumber(2, ',', '.');
    });
  }

  open() {
    if (DcProductBundle.activeInstance && DcProductBundle.activeInstance !== this) DcProductBundle.activeInstance.close({ restoreFocus: false });
    this.layer.hidden = false;
    this.openButton.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('dc-bundle-wizard-open');
    DcProductBundle.activeInstance = this;
    this.goToStep(this.currentStep);
    requestAnimationFrame(() => this.layer.querySelector('[data-dc-bundle-close]')?.focus({ preventScroll: true }));
  }

  close({ restoreFocus = true } = {}) {
    if (!this.layer || this.layer.hidden) return;
    this.closeQuick({ restoreFocus: false });
    this.layer.hidden = true;
    this.openButton.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('dc-bundle-wizard-open');
    if (DcProductBundle.activeInstance === this) DcProductBundle.activeInstance = null;
    if (restoreFocus && this.openButton.isConnected) this.openButton.focus({ preventScroll: true });
  }

  getSelectedOptions() {
    return Array.from(this.layer.querySelectorAll('[data-dc-bundle-option]')).sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
  }

  resolveBundleVariant() {
    const options = this.getSelectedOptions();
    if (options.length === 0) return this.bundleVariants.find((variant) => variant.available) || this.bundleVariants[0] || null;
    return this.bundleVariants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
  }

  updateBundleVariant(event) {
    const fieldset = event.target.closest('[data-dc-bundle-option]');
    fieldset.querySelector('[data-dc-bundle-option-current]').textContent = event.target.value;
    const position = Number(fieldset.dataset.optionPosition);
    const component = Number(fieldset.dataset.component || (position === 0 ? 1 : 2));
    this.updateComponentImage(component, event.target.value);
    this.selectedVariant = this.resolveBundleVariant();
    this.updateBundlePresentation();
  }

  updateVisualSelection(event) {
    const fieldset = event.target.closest('[data-dc-bundle-visual-option]');
    const component = Number(fieldset.dataset.component);
    fieldset.querySelector('[data-dc-bundle-option-current]').textContent = event.target.value;
    const position = Number(fieldset.dataset.optionPosition);
    this.visualSelections[component][position] = event.target.value;
    this.updateComponentImage(component, event.target.value);
  }

  updateComponentImage(component, value) {
    const variants = this.visualVariants[component] || [];
    const match = variants.find((variant) => variant.options.includes(value)) || variants.find((variant) => variant.available) || variants[0];
    const image = this.layer.querySelector(`[data-dc-bundle-component-image="${component}"]`);
    if (image && match?.image) {
      image.src = match.image;
      image.removeAttribute('srcset');
    }
  }

  updateBundlePresentation() {
    const variant = this.selectedVariant;
    const price = this.layer.querySelector('[data-dc-bundle-price]');
    const compare = this.layer.querySelector('[data-dc-bundle-compare]');
    const savings = this.layer.querySelector('[data-dc-bundle-savings]');
    const availability = this.layer.querySelector('[data-dc-bundle-availability]');
    if (!variant) {
      availability.textContent = 'Combinación no disponible';
      availability.classList.add('is-unavailable');
      this.nextButton.disabled = true;
      this.addAllButton.disabled = true;
      return;
    }
    price.textContent = variant.priceFormatted;
    compare.textContent = variant.compareAtFormatted;
    compare.hidden = !(variant.compareAt > variant.price);
    savings.textContent = `Ahorra ${variant.savingsFormatted}`;
    savings.hidden = !(variant.savings > 0);
    availability.textContent = variant.available ? 'Disponible' : 'Agotado';
    availability.classList.toggle('is-unavailable', !variant.available);
    this.nextButton.disabled = !variant.available;
    this.addAllButton.disabled = !variant.available;
  }

  goToStep(index) {
    const safeIndex = Math.min(Math.max(index, 0), this.steps.length - 1);
    this.closeQuick({ restoreFocus: false });
    this.currentStep = safeIndex;
    this.steps.forEach((step, stepIndex) => {
      const active = stepIndex === safeIndex;
      step.hidden = !active;
      step.classList.toggle('is-active', active);
    });
    this.progressButtons.forEach((button, stepIndex) => {
      button.setAttribute('aria-current', stepIndex === safeIndex ? 'step' : 'false');
      button.classList.toggle('is-complete', stepIndex < safeIndex);
    });
    if (safeIndex === 4) this.renderSummary();
    this.updateStepState();
    this.layer.querySelector('.dc-bundle-wizard__work')?.scrollTo({ top: 0, behavior: 'auto' });
  }

  updateStepState() {
    this.backButton.disabled = this.currentStep === 0;
    this.nextButton.hidden = this.currentStep === 4;
    this.addAllButton.hidden = this.currentStep !== 4;
    this.updateBundlePresentation();
  }

  initializeExtraCarousels(signal) {
    this.resizeObservers = [];
    this.layer.querySelectorAll('[data-dc-bundle-extra-carousel]').forEach((carousel) => {
      const viewport = carousel.querySelector('[data-dc-bundle-extra-viewport]');
      const prev = carousel.querySelector('[data-dc-bundle-extra-prev]');
      const next = carousel.querySelector('[data-dc-bundle-extra-next]');
      const navigate = (direction) => {
        const card = viewport.querySelector('[data-dc-bundle-extra-card]');
        const list = viewport.querySelector('.dc-bundle-wizard__extra-list');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
        viewport.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      const update = () => {
        const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        prev.disabled = viewport.scrollLeft <= 1;
        next.disabled = viewport.scrollLeft >= max - 1;
      };
      prev.addEventListener('click', () => navigate(-1), { signal });
      next.addEventListener('click', () => navigate(1), { signal });
      viewport.addEventListener('scroll', update, { signal, passive: true });
      const observer = new ResizeObserver(update);
      observer.observe(viewport);
      this.resizeObservers.push(observer);
      update();
    });
  }

  handleExtraAction(button) {
    const card = button.closest('[data-dc-bundle-extra-card]');
    const group = card.dataset.group;
    const productId = card.dataset.productId;
    if (this.selectedExtras[group].has(productId)) {
      this.selectedExtras[group].delete(productId);
      this.updateExtraCard(card, false);
      return;
    }
    this.openQuick(card, button);
  }

  extraFromCard(card) {
    return { productId: card.dataset.productId, title: card.dataset.productTitle, variantId: Number(card.dataset.variantId), variantTitle: card.dataset.variantTitle, price: Number(card.dataset.price), priceFormatted: card.dataset.priceFormatted, image: card.dataset.image || null };
  }

  updateExtraCard(card, selected) {
    const button = card.querySelector('[data-dc-bundle-extra-action]');
    card.classList.toggle('is-selected', selected);
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
  }

  openQuick(card, trigger) {
    const quick = this.layer.querySelector(`[data-dc-bundle-quick][data-group="${card.dataset.group}"][data-product-id="${card.dataset.productId}"]`);
    if (!quick) return;
    this.activeQuick = quick;
    this.quickTrigger = trigger;
    quick.hidden = false;
    quick.dataset.cardProductId = card.dataset.productId;
    this.updateQuickVariant(quick);
    requestAnimationFrame(() => quick.querySelector('[data-dc-bundle-quick-close]')?.focus({ preventScroll: true }));
  }

  closeQuick({ restoreFocus = true } = {}) {
    if (!this.activeQuick) return;
    this.activeQuick.hidden = true;
    if (restoreFocus && this.quickTrigger?.isConnected) this.quickTrigger.focus({ preventScroll: true });
    this.activeQuick = null;
    this.quickTrigger = null;
  }

  getQuickVariants(quick) {
    try { return JSON.parse(quick.querySelector('[data-dc-bundle-quick-data]')?.textContent || '[]'); }
    catch (error) { return []; }
  }

  updateQuickVariant(quick) {
    if (!quick) return;
    const values = Array.from(quick.querySelectorAll('[data-dc-bundle-quick-option]')).map((select) => select.value);
    const variants = this.getQuickVariants(quick);
    const variant = values.length === 0
      ? variants.find((candidate) => candidate.available) || variants[0] || null
      : variants.find((candidate) => candidate.options.every((value, index) => value === values[index])) || null;
    quick.dcSelectedVariant = variant;
    const button = quick.querySelector('[data-dc-bundle-quick-select]');
    const price = quick.querySelector('[data-dc-bundle-quick-price]');
    const availability = quick.querySelector('[data-dc-bundle-quick-availability]');
    if (!variant) {
      button.disabled = true;
      availability.textContent = 'Combinación no disponible';
      return;
    }
    button.dataset.variantId = variant.id;
    button.disabled = !variant.available;
    price.textContent = variant.priceFormatted;
    availability.textContent = variant.available ? 'Disponible' : 'Agotado';
    const image = quick.querySelector('.dc-bundle-wizard__quick-media img');
    if (image && variant.image) { image.src = variant.image; image.removeAttribute('srcset'); }
  }

  selectQuickVariant(quick) {
    const variant = quick.dcSelectedVariant;
    if (!variant?.available) return;
    const group = quick.dataset.group;
    const card = this.layer.querySelector(`[data-dc-bundle-extra-card][data-group="${group}"][data-product-id="${quick.dataset.productId}"]`);
    const item = this.extraFromCard(card);
    item.variantId = Number(variant.id);
    item.variantTitle = variant.title;
    item.price = Number(variant.price);
    item.priceFormatted = variant.priceFormatted;
    item.image = variant.image || item.image;
    this.selectedExtras[group].set(quick.dataset.productId, item);
    this.updateExtraCard(card, true);
    this.closeQuick();
  }

  renderSummary() {
    const container = this.summaryElement;
    container.replaceChildren();
    if (!this.selectedVariant) return;
    const selectedOptions = this.getSelectedOptions();
    const visualOneDetail = Object.values(this.visualSelections[1]).filter(Boolean).join(' · ');
    const visualTwoDetail = Object.values(this.visualSelections[2]).filter(Boolean).join(' · ');
    const items = [
      { title: this.bundleMeta.title, detail: this.selectedVariant.title, priceFormatted: this.selectedVariant.priceFormatted, image: this.bundleMeta.image, type: 'Bundle' },
      { title: this.bundleMeta.visual1Title, detail: visualOneDetail || selectedOptions[0] || 'Incluido', priceFormatted: 'Incluido', image: this.layer.querySelector('[data-dc-bundle-component-image="1"]')?.src || null, type: 'Componente 1' },
      { title: this.bundleMeta.visual2Title, detail: visualTwoDetail || selectedOptions.slice(1).join(' · ') || 'Incluido', priceFormatted: 'Incluido', image: this.layer.querySelector('[data-dc-bundle-component-image="2"]')?.src || null, type: 'Componente 2' },
      ...this.selectedExtras.addons.values(),
      ...this.selectedExtras.accessories.values()
    ];
    let total = Number(this.selectedVariant.price);
    this.selectedExtras.addons.forEach((item) => { total += Number(item.price); });
    this.selectedExtras.accessories.forEach((item) => { total += Number(item.price); });
    items.forEach((item) => container.appendChild(this.createSummaryItem(item)));
    const totalRow = document.createElement('div');
    totalRow.className = 'dc-bundle-wizard__summary-total';
    const label = document.createElement('strong'); label.textContent = 'Total';
    const value = document.createElement('strong'); value.textContent = this.formatMoney(total);
    totalRow.append(label, value); container.appendChild(totalRow);
  }

  createSummaryItem(item) {
    const row = document.createElement('article'); row.className = 'dc-bundle-wizard__summary-item';
    if (item.image) { const img = document.createElement('img'); img.src = item.image; img.alt = ''; img.width = 120; img.height = 120; row.appendChild(img); }
    const copy = document.createElement('div');
    const title = document.createElement('strong'); title.textContent = item.title;
    const detail = document.createElement('span'); detail.textContent = item.detail || item.variantTitle || '';
    const price = document.createElement('span'); price.textContent = item.priceFormatted || '';
    copy.append(title, detail, price); row.appendChild(copy); return row;
  }

  formatMoney(cents) {
    const format = window.theme?.moneyFormat || '{{amount}}';
    const decimal = (Number(cents) / 100).toFixed(2);
    const withComma = decimal.replace('.', ',');
    return format.replace(/\{\{\s*(amount_no_decimals_with_comma_separator|amount_with_comma_separator|amount_no_decimals|amount)\s*\}\}/, (match, token) => {
      if (token === 'amount_no_decimals') return Math.round(Number(cents) / 100).toString();
      if (token === 'amount_no_decimals_with_comma_separator') return Math.round(Number(cents) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      if (token === 'amount_with_comma_separator') return withComma;
      return decimal;
    });
  }

  async addAllToCart() {
    if (!this.selectedVariant?.available || this.addAllButton.disabled) return;
    const items = [{ id: Number(this.selectedVariant.id), quantity: 1 }];
    this.selectedExtras.addons.forEach((item) => items.push({ id: Number(item.variantId), quantity: 1 }));
    this.selectedExtras.accessories.forEach((item) => items.push({ id: Number(item.variantId), quantity: 1 }));
    this.addAllButton.disabled = true;
    this.errorElement.hidden = true;
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    try {
      const response = await fetch(`${window.routes.cartAddUrl}.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'No se pudo agregar el bundle.');
      this.close({ restoreFocus: false });
      this.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { button: this.addAllButton, variant: this.selectedVariant, quantity: items.length, productAdded: true } }));
    } catch (error) {
      this.errorElement.textContent = error.message;
      this.errorElement.hidden = false;
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      this.addAllButton.disabled = false;
    }
  }

  onKeydown(event) {
    if (DcProductBundle.activeInstance !== this || this.layer.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.activeQuick) this.closeQuick(); else this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const root = this.activeQuick?.querySelector('.dc-bundle-wizard__quick-panel') || this.modal;
    const focusable = Array.from(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (!focusable.length) { event.preventDefault(); root.focus(); return; }
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
}
if (!customElements.get('dc-product-gallery')) {
  customElements.define('dc-product-gallery', DcProductGallery);
}


if (!customElements.get('dc-product-variant-selector')) {
  customElements.define('dc-product-variant-selector', DcProductVariantSelector);
}
if (!customElements.get('dc-product-accessories')) {
  customElements.define('dc-product-accessories', DcProductAccessories);
}
if (!customElements.get('dc-product-bundle')) {
  customElements.define('dc-product-bundle', DcProductBundle);
}
if (!customElements.get('dc-product-payment-options')) {
  customElements.define('dc-product-payment-options', DcProductPaymentOptions);
}
